import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { vendorObsApi, type VendorActivityQuery } from '@/services/vendor.api';

const PAGE_SIZE = 25;

const STATUS_TONE: Record<string, string> = {
  success: 'text-emerald-400',
  failed: 'text-red-400',
  partial: 'text-amber-400',
};

const inputCls =
  'rounded-md border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-white placeholder:text-slate-500';

/** Masked recent-activity feed (F2) from the channel sync log. */
export function VendorActivityPage() {
  const [sp] = useSearchParams();

  // Seed from the drill-down link (?propertyId=&status=), then own the state.
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(sp.get('status') ?? '');
  const [channel, setChannel] = useState('');
  const [type, setType] = useState('');
  const [propertyId, setPropertyId] = useState(sp.get('propertyId') ?? '');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [offset, setOffset] = useState(0);
  const [applied, setApplied] = useState(0);

  const query: VendorActivityQuery = {
    limit: PAGE_SIZE,
    offset,
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(status ? { status } : {}),
    ...(channel.trim() ? { channel: channel.trim() } : {}),
    ...(type.trim() ? { type: type.trim() } : {}),
    ...(propertyId.trim() ? { propertyId: propertyId.trim() } : {}),
    ...(fromDate ? { fromDate } : {}),
    ...(toDate ? { toDate } : {}),
  };

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['vendor', 'activity', query, applied],
    queryFn: () => vendorObsApi.activity(query),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });

  const resetPageAnd = (fn: () => void) => {
    setOffset(0);
    fn();
  };

  const applyText = () => {
    setOffset(0);
    setApplied((n) => n + 1);
    refetch();
  };

  const total = data?.total ?? 0;
  const rows = data?.rows ?? [];
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="min-w-0 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-white">Activity feed</h1>
        <button
          onClick={() => refetch()}
          className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"
        >
          {isFetching ? '…' : 'Refresh'}
        </button>
      </div>

      {/* ── Filters ──────────────────────────────────────────────── */}
      <div className="rounded-lg border border-slate-800 bg-slate-900 p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-slate-500">
            Search
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyText()}
              placeholder="hotel / error…"
              className={`${inputCls} w-full`}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Status
            <select
              value={status}
              onChange={(e) => resetPageAnd(() => setStatus(e.target.value))}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="failed">Failed</option>
              <option value="partial">Partial</option>
              <option value="success">Success</option>
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Channel
            <input
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyText()}
              placeholder="any"
              className={`${inputCls} w-28`}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Type
            <input
              value={type}
              onChange={(e) => setType(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyText()}
              placeholder="any"
              className={`${inputCls} w-28`}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-500">
            From
            <input
              type="date"
              value={fromDate}
              onChange={(e) => resetPageAnd(() => setFromDate(e.target.value))}
              className={inputCls}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            To
            <input
              type="date"
              value={toDate}
              onChange={(e) => resetPageAnd(() => setToDate(e.target.value))}
              className={inputCls}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Property ID
            <input
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyText()}
              placeholder="uuid (optional)"
              className={inputCls}
            />
          </label>

          <button
            onClick={applyText}
            className="rounded-md bg-amber-500 px-3 py-1.5 text-sm font-semibold text-slate-950 hover:bg-amber-400"
          >
            Apply
          </button>
        </div>
      </div>

      {isError && (
        <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">
          Could not load activity.
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────── */}
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">Hotel</th>
              <th className="px-3 py-2">Channel</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Error</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800 bg-slate-950">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                  No events match these filters.
                </td>
              </tr>
            )}
            {rows.map((e) => (
              <tr key={e.id} className="hover:bg-slate-900/60">
                <td className="whitespace-nowrap px-3 py-2 text-slate-400">
                  {new Date(e.time).toLocaleString()}
                </td>
                <td className="max-w-[14rem] truncate px-3 py-2 text-slate-200" title={e.hotel}>
                  {e.hotel}
                </td>
                <td className="px-3 py-2 text-slate-300">{e.channel}</td>
                <td className="px-3 py-2 text-slate-300">{e.type}</td>
                <td
                  className={`px-3 py-2 font-medium ${STATUS_TONE[e.status] ?? 'text-slate-300'}`}
                >
                  {e.status}
                </td>
                <td className="max-w-xs truncate px-3 py-2 text-slate-500" title={e.error ?? ''}>
                  {e.error ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
        <span>
          {from}–{to} of {total.toLocaleString()}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            disabled={offset === 0}
            className="rounded-md bg-slate-800 px-3 py-1.5 text-slate-200 hover:bg-slate-700 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            disabled={to >= total}
            className="rounded-md bg-slate-800 px-3 py-1.5 text-slate-200 hover:bg-slate-700 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
